import { SplitTreatmentName } from "@yardzen/splitio";
import { getPartnerBranding } from "./partnerBranding";

/**
 * Predicate for "this design project belongs to a builder partner". Mirrors
 * the API self-assign queue rule (`partnerId != null`): a project with a
 * partner attached is a partner project regardless of matchStatus, while
 * NO_MATCH / NEEDS_SUPPORT rows without a partner are not.
 */
export function isBuilderPartnerProject(
  context: { partnerKey: string | null } | null | undefined,
): boolean {
  return context?.partnerKey != null;
}

/**
 * Where a builder-partner client enters the co-branded flow, or null to stay
 * in standard onboarding (no partner, kill switch off, or the partner's route
 * group hasn't shipped). Always the onboarding entry — the co-branded flow
 * gates account set-up itself, so returning clients aren't bounced through a
 * separate step.
 */
export function getBuilderPartnerOnboardingPath(args: {
  designProjectId: string;
  partnerKey: string | null | undefined;
  onboardingSplitOn: boolean;
}): string | null {
  const { designProjectId, partnerKey, onboardingSplitOn } = args;
  if (!onboardingSplitOn || partnerKey == null) {
    return null;
  }
  const basePath = getPartnerBranding(partnerKey)?.onboardingBasePath;
  if (!basePath) {
    return null;
  }
  return `${basePath}/design-project/${designProjectId}/onboarding`;
}

/**
 * The split that gates a partner's co-branded onboarding, resolved from the
 * branding registry so the routing logic never names a partner-specific split.
 * Null when the partner is unknown or hasn't configured one.
 */
export function getBuilderPartnerOnboardingSplit(
  partnerKey: string | null | undefined,
): SplitTreatmentName | null {
  return getPartnerBranding(partnerKey)?.onboardingSplit ?? null;
}

/**
 * Navigates into a builder-partner co-branded flow. Partner route groups
 * (e.g. (toll)) are separate Next.js root layouts from the standard (default)
 * group, and the App Router can't client-side navigate across root layouts —
 * router.replace() silently no-ops and strands the user on a loader. A full
 * document load is required, so use window.location rather than the router.
 */
export function navigateToBuilderPartnerFlow(path: string): void {
  window.location.replace(path);
}

/**
 * Client-side split check via the /api/split-treatment route. Fails closed
 * (false = kill-switch-off semantics) on any error or non-boolean body, so a
 * split evaluation that errors doesn't strand a client outside the standard
 * flow.
 */
export async function fetchTreatmentOnOff(
  name: SplitTreatmentName,
): Promise<boolean> {
  try {
    const response = await fetch("/api/split-treatment", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ splitTreatmentName: name }),
    });
    if (!response.ok) {
      return false;
    }
    return (await response.json()) === true;
  } catch {
    return false;
  }
}
